// ============================================================================
// Google Calendar — events CRUD + listing helpers.
//
// All endpoints live under `GET/POST/PATCH/DELETE /calendar/v3/calendars/{calendarId}/events`.
// We always:
//   - URL-encode `calendarId` (it's often an email address with `@`; raw `@`
//     in the path can break routing on some proxies).
//   - Put the access token in the Authorization header, NEVER the URL.
//   - Use PATCH (not PUT) for updates so partial updates work — PUT requires
//     the full event body and would clobber fields the caller didn't send.
//
// Canonical docs:
//   - https://developers.google.com/calendar/api/v3/reference/events
//   - https://developers.google.com/calendar/api/guides/sync  (incremental sync)
// ============================================================================

import type {
  CalendarEvent,
  CalendarEventInput,
  ListEventsResult,
} from '../../capabilities/calendar'
import { GOOGLE_CALENDAR_API_BASE } from './oauth'
import {
  GoogleCalendarError,
  classifyGoogleError,
  parseGoogleRetryAfterMs,
  type GoogleErrorEnvelope,
  type GoogleEventRaw,
  type GoogleEventsListResponse,
} from './types'

// ============================================================================
// Constants
// ============================================================================

/** Default per-page size. Google's max is 2500 but 250 is saner for latency. */
const DEFAULT_MAX_RESULTS = 250
/** Google's hard cap per call. */
const GOOGLE_MAX_RESULTS_CEILING = 2500

// ============================================================================
// listGoogleEvents
// ============================================================================

export interface ListGoogleEventsInput {
  accessToken: string
  calendarId: string
  timeMin?: string
  timeMax?: string
  pageToken?: string
  maxResults?: number
  /**
   * Incremental sync token. When present the request omits `timeMin`/`timeMax`
   * (Google rejects mixing them) and returns only events that changed since
   * the last full-sync snapshot. Google returns `410 gone +
   * reason=fullSyncRequired` when the token has expired (~30d).
   */
  syncToken?: string
  /** When true, return cancelled events too. Needed for incremental sync. */
  showDeleted?: boolean
  /** Expand recurring events into single instances (default: false). */
  singleEvents?: boolean
  /** Override the API base URL (tests). */
  apiBase?: string
}

/**
 * List events from a calendar. Supports both time-windowed queries and
 * incremental sync via `syncToken`.
 */
export async function listGoogleEvents(
  input: ListGoogleEventsInput,
  fetchImpl: typeof fetch = fetch,
): Promise<ListEventsResult> {
  assertAccessToken(input.accessToken)
  assertCalendarId(input.calendarId)

  const base = input.apiBase ?? GOOGLE_CALENDAR_API_BASE
  const url = new URL(`${base}/calendars/${encodeURIComponent(input.calendarId)}/events`)

  if (input.syncToken) {
    // Google disallows `timeMin` / `timeMax` alongside `syncToken`; the
    // caller's intent is incremental and the window is implicit.
    url.searchParams.set('syncToken', input.syncToken)
  } else {
    if (input.timeMin) url.searchParams.set('timeMin', input.timeMin)
    if (input.timeMax) url.searchParams.set('timeMax', input.timeMax)
  }
  if (input.pageToken) url.searchParams.set('pageToken', input.pageToken)
  if (typeof input.maxResults === 'number') {
    url.searchParams.set(
      'maxResults',
      String(clampMaxResults(input.maxResults)),
    )
  } else {
    url.searchParams.set('maxResults', String(DEFAULT_MAX_RESULTS))
  }
  if (input.showDeleted === true) url.searchParams.set('showDeleted', 'true')
  if (input.singleEvents === true) url.searchParams.set('singleEvents', 'true')

  const resp = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp, 'event')
  }

  const body = (await resp.json().catch(() => null)) as
    | GoogleEventsListResponse
    | null
  if (!body) {
    throw new GoogleCalendarError(
      'PROVIDER_ERROR',
      'Google events.list returned an unexpected body',
    )
  }

  const items = Array.isArray(body.items) ? body.items : []
  const events = items.map((raw) => mapEvent(raw, input.calendarId))

  const out: ListEventsResult = { events }
  if (typeof body.nextPageToken === 'string') out.nextPageToken = body.nextPageToken
  if (typeof body.nextSyncToken === 'string') out.nextSyncToken = body.nextSyncToken
  return out
}

// ============================================================================
// getGoogleEvent
// ============================================================================

export interface GetGoogleEventInput {
  accessToken: string
  calendarId: string
  eventId: string
  apiBase?: string
}

export async function getGoogleEvent(
  input: GetGoogleEventInput,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarEvent> {
  assertAccessToken(input.accessToken)
  assertCalendarId(input.calendarId)
  assertEventId(input.eventId)

  const base = input.apiBase ?? GOOGLE_CALENDAR_API_BASE
  const url = `${base}/calendars/${encodeURIComponent(input.calendarId)}/events/${encodeURIComponent(input.eventId)}`

  const resp = await fetchImpl(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp, 'event')
  }

  const raw = (await resp.json().catch(() => null)) as GoogleEventRaw | null
  if (!raw || typeof raw.id !== 'string') {
    throw new GoogleCalendarError(
      'PROVIDER_ERROR',
      'Google events.get returned an unexpected body',
    )
  }
  return mapEvent(raw, input.calendarId)
}

// ============================================================================
// createGoogleEvent
// ============================================================================

export interface CreateGoogleEventInput {
  accessToken: string
  calendarId: string
  event: CalendarEventInput
  apiBase?: string
}

export async function createGoogleEvent(
  input: CreateGoogleEventInput,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarEvent> {
  assertAccessToken(input.accessToken)
  assertCalendarId(input.calendarId)
  if (!input.event || typeof input.event !== 'object') {
    throw new GoogleCalendarError('INVALID_INPUT', 'event is required')
  }

  const base = input.apiBase ?? GOOGLE_CALENDAR_API_BASE
  const url = `${base}/calendars/${encodeURIComponent(input.calendarId)}/events`

  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
    body: JSON.stringify(input.event),
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp, 'event')
  }

  const raw = (await resp.json().catch(() => null)) as GoogleEventRaw | null
  if (!raw || typeof raw.id !== 'string') {
    throw new GoogleCalendarError(
      'PROVIDER_ERROR',
      'Google events.insert returned an unexpected body',
    )
  }
  return mapEvent(raw, input.calendarId)
}

// ============================================================================
// updateGoogleEvent — PATCH so partial updates work
// ============================================================================

export interface UpdateGoogleEventInput {
  accessToken: string
  calendarId: string
  eventId: string
  patch: Partial<CalendarEventInput>
  apiBase?: string
}

export async function updateGoogleEvent(
  input: UpdateGoogleEventInput,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarEvent> {
  assertAccessToken(input.accessToken)
  assertCalendarId(input.calendarId)
  assertEventId(input.eventId)
  if (!input.patch || typeof input.patch !== 'object') {
    throw new GoogleCalendarError('INVALID_INPUT', 'patch is required')
  }

  const base = input.apiBase ?? GOOGLE_CALENDAR_API_BASE
  const url = `${base}/calendars/${encodeURIComponent(input.calendarId)}/events/${encodeURIComponent(input.eventId)}`

  // PATCH semantics: Google preserves fields omitted from the body. PUT
  // would require echoing the full event shape back, which the caller
  // cannot be expected to construct faithfully.
  const resp = await fetchImpl(url, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
    body: JSON.stringify(input.patch),
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp, 'event')
  }

  const raw = (await resp.json().catch(() => null)) as GoogleEventRaw | null
  if (!raw || typeof raw.id !== 'string') {
    throw new GoogleCalendarError(
      'PROVIDER_ERROR',
      'Google events.patch returned an unexpected body',
    )
  }
  return mapEvent(raw, input.calendarId)
}

// ============================================================================
// deleteGoogleEvent
// ============================================================================

export interface DeleteGoogleEventInput {
  accessToken: string
  calendarId: string
  eventId: string
  apiBase?: string
}

export async function deleteGoogleEvent(
  input: DeleteGoogleEventInput,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  assertAccessToken(input.accessToken)
  assertCalendarId(input.calendarId)
  assertEventId(input.eventId)

  const base = input.apiBase ?? GOOGLE_CALENDAR_API_BASE
  const url = `${base}/calendars/${encodeURIComponent(input.calendarId)}/events/${encodeURIComponent(input.eventId)}`

  const resp = await fetchImpl(url, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
  })

  // Google returns 204 (no body) on success. 410 is "already gone" — we treat
  // that as success too because delete is idempotent from the caller's POV.
  if (resp.ok || resp.status === 204 || resp.status === 410) return
  throw await errorFromResponse(resp, 'event')
}

// ============================================================================
// Mapping — Google raw → capability CalendarEvent
// ============================================================================

function mapEvent(raw: GoogleEventRaw, calendarId: string): CalendarEvent {
  const status = normaliseStatus(raw.status)
  const out: CalendarEvent = {
    id: raw.id,
    iCalUID: typeof raw.iCalUID === 'string' ? raw.iCalUID : raw.id,
    calendarId,
    summary: typeof raw.summary === 'string' ? raw.summary : '',
    start: {
      ...(typeof raw.start?.dateTime === 'string' ? { dateTime: raw.start.dateTime } : {}),
      ...(typeof raw.start?.date === 'string' ? { date: raw.start.date } : {}),
      ...(typeof raw.start?.timeZone === 'string' ? { timeZone: raw.start.timeZone } : {}),
    },
    end: {
      ...(typeof raw.end?.dateTime === 'string' ? { dateTime: raw.end.dateTime } : {}),
      ...(typeof raw.end?.date === 'string' ? { date: raw.end.date } : {}),
      ...(typeof raw.end?.timeZone === 'string' ? { timeZone: raw.end.timeZone } : {}),
    },
  }
  if (typeof raw.description === 'string') out.description = raw.description
  if (typeof raw.location === 'string') out.location = raw.location
  if (Array.isArray(raw.attendees)) {
    const mapped = raw.attendees
      .filter((a) => typeof a?.email === 'string' && a.email.length > 0)
      .map((a) => ({
        email: a.email as string,
        ...(typeof a.displayName === 'string' ? { displayName: a.displayName } : {}),
        ...(typeof a.responseStatus === 'string' ? { responseStatus: a.responseStatus } : {}),
      }))
    if (mapped.length > 0) out.attendees = mapped
  }
  if (status) out.status = status
  if (typeof raw.created === 'string') out.created = raw.created
  if (typeof raw.updated === 'string') out.updated = raw.updated
  if (typeof raw.htmlLink === 'string') out.htmlLink = raw.htmlLink
  return out
}

function normaliseStatus(
  value: string | undefined,
): CalendarEvent['status'] | undefined {
  if (value === 'confirmed' || value === 'tentative' || value === 'cancelled') {
    return value
  }
  return undefined
}

// ============================================================================
// Internals
// ============================================================================

async function errorFromResponse(
  resp: Response,
  resourceKind: 'calendar' | 'event' | 'watch' | 'other',
): Promise<GoogleCalendarError> {
  let envelope: GoogleErrorEnvelope | null = null
  try {
    envelope = (await resp.json()) as GoogleErrorEnvelope
  } catch {
    // Non-JSON error body — leave envelope null.
  }
  const retryAfterMs = parseGoogleRetryAfterMs(resp.headers.get('Retry-After'))
  return classifyGoogleError(resp.status, envelope, resourceKind, retryAfterMs)
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GoogleCalendarError('INVALID_INPUT', 'accessToken is required')
  }
}

function assertCalendarId(calendarId: string | undefined | null): void {
  if (!calendarId || typeof calendarId !== 'string' || calendarId.trim().length === 0) {
    throw new GoogleCalendarError('INVALID_INPUT', 'calendarId is required')
  }
}

function assertEventId(eventId: string | undefined | null): void {
  if (!eventId || typeof eventId !== 'string' || eventId.trim().length === 0) {
    throw new GoogleCalendarError('INVALID_INPUT', 'eventId is required')
  }
}

function clampMaxResults(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_MAX_RESULTS
  return Math.min(Math.floor(n), GOOGLE_MAX_RESULTS_CEILING)
}
